import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { PdfField } from "../types";
import { EditorMode } from "../types";
import { FieldsList } from "./FieldsList";
import { CreateTemplateDialog } from "./CreateTemplateDialog";
import { RoutePath } from "@/section/router";
import {
  PdfDocumentViewer,
  PdfFieldCanvas,
  PdfFieldOverlay,
  PdfSamplePreview,
  usePdfFieldManager,
  type PdfOverlayRender,
  type PdfSource,
  type PdfFieldMetaShape,
} from "@/section/pdf";
import {
  achFieldToPdfFieldRect,
  pdfFieldRectToAchField,
  pdfFieldRectsToAchFields,
} from "./fieldTransforms";

const PDF_WIDTH = 610;

function mergeAchField(base: PdfField, updates: Partial<PdfField>): PdfField {
  return {
    ...base,
    ...updates,
    position_data: {
      ...base.position_data,
      ...updates.position_data,
    },
  };
}

export function PdfFieldEditor() {
  const navigate = useNavigate();
  const [pdfSource, setPdfSource] = useState<PdfSource | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [mode, setMode] = useState<EditorMode>("edit");
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  const fieldManager = usePdfFieldManager({
    defaultMetaFactory: ({ index }) =>
      ({
        label: `Field ${index + 1}`,
        value: "",
        ach: {
          fieldKey: `Field ${index + 1}`,
          fieldType: "system",
          systemFieldType: "system_reference_field",
          isRequired: false,
          isReadonly: false,
        },
      }) satisfies PdfFieldMetaShape,
  });

  const {
    fields,
    selectedId,
    selectField,
    addField,
    updateField,
    removeField,
    sampleValues,
    sampleList,
    setSampleValue,
    reset,
  } = fieldManager;

  const sampleAwareFields = useMemo(() => {
    return fields.map((field) => {
      const sampleValue = sampleValues[field.id];
      if (typeof sampleValue === "string") {
        return {
          ...field,
          meta: {
            ...(field.meta ?? {}),
            value: sampleValue,
          },
        };
      }
      return field;
    });
  }, [fields, sampleValues]);

  const achFields = useMemo(() => pdfFieldRectsToAchFields(sampleAwareFields), [sampleAwareFields]);

  const handleFileUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || file.type !== "application/pdf") {
        return;
      }

      setPdfSource(file);
      setOriginalFile(file);
      setCurrentPage(1);
      reset([]);
    },
    [reset],
  );

  const handleDocumentLoadSuccess = useCallback(
    ({ totalPages }: { totalPages: number }) => {
      if (currentPage > totalPages) {
        setCurrentPage(totalPages);
      }
    },
    [currentPage],
  );

  const handleUpdateField = useCallback(
    (fieldId: string, updates: Partial<PdfField>) => {
      updateField(fieldId, (rect) => {
        const currentSample = sampleValues[rect.id];
        const rectWithSample =
          typeof currentSample === "string"
            ? {
                ...rect,
                meta: {
                  ...(rect.meta ?? {}),
                  value: currentSample,
                },
              }
            : rect;
        const current = pdfFieldRectToAchField(rectWithSample);
        const next = mergeAchField(current, updates);
        const nextRect = achFieldToPdfFieldRect(next);

        if (updates.value !== undefined) {
          setSampleValue(nextRect.id, updates.value ?? "");
        }

        return nextRect;
      });
    },
    [sampleValues, setSampleValue, updateField],
  );

  const handleDeleteField = useCallback(
    (fieldId: string) => {
      removeField(fieldId);
    },
    [removeField],
  );

  const handleOverlaySelect = useCallback(
    (fieldId: string | null) => {
      selectField(fieldId);
    },
    [selectField],
  );

  const handleOverlayFieldUpdate = useCallback(
    (fieldId: string, updates: { x: number; y: number }) => {
      const rect = fields.find((f) => f.id === fieldId);
      if (!rect) return;

      const currentSample = sampleValues[rect.id];
      const rectWithSample =
        typeof currentSample === "string"
          ? {
              ...rect,
              meta: {
                ...(rect.meta ?? {}),
                value: currentSample,
              },
            }
          : rect;

      const current = pdfFieldRectToAchField(rectWithSample);
      const next = mergeAchField(current, {
        position_data: {
          ...current.position_data,
          x: updates.x,
          y: updates.y,
        },
      });
      const nextRect = achFieldToPdfFieldRect(next);

      updateField(fieldId, () => nextRect);
    },
    [fields, sampleValues, updateField],
  );

  const overlayRenderer = useCallback<PdfOverlayRender>(
    (metrics) => {
      const pageFields = sampleAwareFields.filter((field) => field.page === metrics.page);
      const rawPageFields = fields.filter((field) => field.page === metrics.page);

      return (
        <>
          <PdfFieldOverlay
            fields={pageFields}
            metrics={metrics}
            selectedId={selectedId}
            onSelect={(field) => handleOverlaySelect(field.id)}
            onUpdateField={mode === "edit" ? handleOverlayFieldUpdate : undefined}
            draggingEnabled={mode === "edit"}
          />
          {mode === "edit" ? (
            <PdfFieldCanvas
              metrics={metrics}
              fields={rawPageFields}
              onCreateRect={(rect) => addField(metrics.page, rect)}
              onSelectField={(field) => handleOverlaySelect(field?.id ?? null)}
            />
          ) : null}
        </>
      );
    },
    [addField, fields, handleOverlaySelect, mode, sampleAwareFields, selectedId],
  );

  const handleOnSuccess = useCallback(() => {
    navigate(RoutePath.AchDocumentsTemplates, { replace: true });
  }, [navigate]);

  const disabledPreview = fields.length === 0;

  return (
    <div className="flex h-full flex-col">
      <div className="p-4 border-b bg-white flex flex-row gap-4 justify-between ">
        <div className="flex flex-row items-center">
          <h2 className="text-2xl font-bold">Create ACH Template</h2>
          <Button
            className="ml-4"
            variant={"default"}
            disabled={fields.length === 0 || !originalFile}
            onClick={() => setDialogOpen(true)}
          >
            Submit Template
          </Button>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="">
            <Input type="file" accept=".pdf" onChange={handleFileUpload} className="max-w-md" />
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant={"outline"}
              className={mode === "edit" ? "bg-yellow-200 hover:bg-yellow-300 text-gray-800 border-yellow-300" : ""}
              onClick={() => setMode("edit")}
            >
              Edit Mode
            </Button>
            <Button
              variant={"outline"}
              className={mode === "preview" ? "bg-yellow-200 hover:bg-yellow-300 text-gray-800 border-yellow-300" : ""}
              onClick={() => setMode("preview")}
              disabled={disabledPreview}
            >
              Preview Mode
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden rounded-lg border bg-background shadow-sm max-h-[100%]">
        <div className="w-full border-r bg-muted/20 lg:w-1/3 overflow-y-scroll">
          <FieldsList
            fields={achFields}
            selectedField={selectedId}
            onSelectField={handleOverlaySelect}
            onUpdateField={handleUpdateField}
            onDeleteField={handleDeleteField}
            mode={mode}
          />
        </div>

        <div className="flex-1 bg-muted/30 overflow-hidden">
          {pdfSource ? (
            mode === "edit" ? (
              <PdfDocumentViewer
                source={pdfSource}
                width={PDF_WIDTH}
                page={currentPage}
                onPageChange={setCurrentPage}
                onDocumentLoad={handleDocumentLoadSuccess}
                renderOverlay={overlayRenderer}
              />
            ) : (
              <PdfSamplePreview
                source={pdfSource}
                fields={fields}
                samples={sampleList}
                width={PDF_WIDTH}
                page={currentPage}
                onPageChange={setCurrentPage}
                onDocumentLoad={handleDocumentLoadSuccess}
              />
            )
          ) : (
            <div className="h-full flex items-center justify-center text-gray-500">
              <div className="text-center">
                <p className="text-lg mb-2">No PDF loaded</p>
                <p className="text-sm">Upload a PDF file to begin editing</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {dialogOpen && originalFile ? (
        <CreateTemplateDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          fields={achFields}
          originalFile={originalFile}
          onSuccess={handleOnSuccess}
        />
      ) : null}
    </div>
  );
}




